// UNIT ID & referral link: client-side checks that mirror the server (server/src/validate.ts).
// They only give early, friendly messages; the API is the authority and validates again.
// Both values are typed by the member. Nothing is generated, derived or tracked.

const UNSAFE = /[\p{Cc}\p{Cf}]/u;
export const UNIT_ID_MAX = 64;
export const REFERRAL_MAX = 500;

export type UnitReferralError = 'unit_id_invalid' | 'unit_id_too_long' | 'url_not_https' | 'url_invalid' | 'url_too_long' | 'url_host_not_allowed';

export const UNIT_REFERRAL_MESSAGES: Record<UnitReferralError, string> = {
  unit_id_invalid: 'Use letters, numbers and normal punctuation only.',
  unit_id_too_long: `Keep the UNIT ID to ${UNIT_ID_MAX} characters.`,
  url_not_https: 'Use the full link starting with https://',
  url_invalid: 'This doesn’t look like a web link. Paste the full referral link.',
  url_too_long: `Keep the link to ${REFERRAL_MAX} characters.`,
  url_host_not_allowed: 'Use your UNIT referral link (a unit.network address).',
};

export function checkUnitId(raw: string): UnitReferralError | null {
  const t = raw.trim();
  if (!t) return null;
  if (t.length > UNIT_ID_MAX) return 'unit_id_too_long';
  return UNSAFE.test(t) ? 'unit_id_invalid' : null;
}

export function checkReferralUrl(raw: string): UnitReferralError | null {
  const t = raw.trim();
  if (!t) return null;
  if (t.length > REFERRAL_MAX) return 'url_too_long';
  if (UNSAFE.test(t) || /\s/.test(t)) return 'url_invalid';
  let u: URL;
  try {
    u = new URL(t);
  } catch {
    return /^[a-z][a-z0-9+.-]*:/i.test(t) ? 'url_invalid' : 'url_not_https';
  }
  if (u.protocol !== 'https:') return 'url_not_https';
  if (u.username || u.password || u.port || !u.hostname.includes('.')) return 'url_invalid';
  return null;
}

/** Short display form, e.g. "example.org/r/abc" (text only; never rendered as HTML). */
export function displayLink(url: string): string {
  try {
    const u = new URL(url);
    const rest = `${u.pathname === '/' ? '' : u.pathname}${u.search}`;
    return `${u.hostname}${rest}`;
  } catch {
    return url;
  }
}
